<template>
  <div class="the-hub container-fluid main-content">
    <div class="row">
      <TheHubSidebar />

      <section class="col-12 col-md-9">
        <Carousel3Images v-if="banners.length" :id="`the-hub::${expiresAt}`" :banners="banners" location-id="the_hub_hero_banner" :timestamp="timestamp" />

        <div class="intro-text">
          <p>
            Welcome to The Hub, the <strong>magazine.co.uk</strong> blog. It’s packed with news, features and fabulous reviews from our wonderful
            <strong>magazine.co.uk</strong> Blogger Network. Plus, there are heaps of competitions, gift guides and offers on our best-selling subscriptions. So if you like your
            magazines, you’re going to love The Hub!
          </p>
        </div>

        <TheHubArticleList :articles="articles" :loading="$fetchState.pending" :error="error" :total-pages="totalPages" :page="page" @change-page="onChangePage" @retry="$fetch" />
      </section>
    </div>
  </div>
</template>

<script>
import { fetchArticles, parsePagination } from '~/lib/util/articles'
import { callAWSFrontendAPI } from '~/lib/util/aws'
import { activeBannersImages } from '~/lib/util/page'

export default {
  name: 'TheHubArticles',
  layout: 'TheHub',
  components: {
    Carousel3Images: () => import('~/components/common/Carousel3Images.vue'),
    TheHubSidebar: () => import('~/components/TheHub/Sidebar.vue'),
    TheHubArticleList: () => import('~/components/TheHub/ArticleList.vue')
  },

  async fetch() {
    const { page, limit } = parsePagination(this.$route.query)
    const useCache = this.$route.query.useCache !== 'false'

    // Same hero banners as the homepage. The helper commits a 404/500 on failure, so reset it afterwards: banners are optional here.
    const home = await callAWSFrontendAPI({ context: this, path: 'home', query: this.$route.query, useCache, type: 'CATEGORYPAGE' })
    this.banners = home?.statusCode === 200 ? activeBannersImages({ banners: home.banners, date: this.$route.query.date || undefined, addExtraImage: true }) : []
    this.expiresAt = home?.expiresAt
    if (process.server) {
      this.$nuxt.context.res.statusCode = 200
    }
    this.$store.commit('UI/statusCode', 200, { root: true })

    const result = await fetchArticles({ context: this, page, limit })
    this.error = result.statusCode !== 200
    this.articles = result.items
    this.total = result.total
    this.page = page
    this.limit = limit
  },

  data() {
    return {
      articles: [],
      banners: [],
      expiresAt: undefined,
      total: 0,
      page: 1,
      limit: parsePagination(this.$route.query).limit,
      error: false,
      timestamp: this.$store.getters['utility/timestamp']
    }
  },

  computed: {
    totalPages() {
      return Math.ceil(this.total / this.limit)
    }
  },
  // Not watchQuery: in Nuxt 2 it resets data() but does not re-run the fetch() hook, leaving an empty list.
  watch: {
    '$route.query': '$fetch'
  },

  methods: {
    onChangePage(page) {
      const query = {}
      if (page > 1) query.page = page
      if (this.$route.query.limit) query.limit = this.limit
      if (this.$route.query.useCache === 'false') query.useCache = 'false'
      this.$router.push({ path: '/the-hub', query })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  },

  head() {
    const baseUrl = `${this.$config.baseURL}/the-hub`
    const pageUrl = page => (page > 1 ? `${baseUrl}?page=${page}` : baseUrl)
    const url = pageUrl(this.page)
    const description = 'The Hub, the magazine.co.uk blog: news, features, reviews, gift guides and offers for magazine lovers.'
    const link = [{ hid: 'canonical', rel: 'canonical', href: url }]
    if (this.page > 1) link.push({ hid: 'prev', rel: 'prev', href: pageUrl(this.page - 1) })
    if (this.page < this.totalPages) link.push({ hid: 'next', rel: 'next', href: pageUrl(this.page + 1) })

    return {
      title: `The Hub${this.page > 1 ? ` - Page ${this.page}` : ''} | magazine.co.uk`,
      meta: [
        { hid: 'description', name: 'description', content: description },
        { hid: 'keywords', name: 'keywords', content: 'Magazine.co.uk' },
        { hid: 'robots', name: 'robots', content: this.$config.robots || 'INDEX,FOLLOW' },
        { hid: 'og:locale', property: 'og:locale', content: 'en_GB' },
        { hid: 'og:type', property: 'og:type', content: 'article' },
        { hid: 'og:title', property: 'og:title', content: 'The Hub' },
        { hid: 'og:description', property: 'og:description', content: description },
        { hid: 'og:url', property: 'og:url', content: url },
        { hid: 'og:site_name', property: 'og:site_name', content: 'magazine.co.uk' }
      ],
      link
    }
  }
}
</script>

<style lang="scss" scoped>
.the-hub {
  max-width: 1200px;
  margin: 0 auto;
}
</style>
